import React, { useState, useEffect } from "react";
import "./DDReportCard.scss";

const LANGS = [
  { id: "es", lbl: "🇪🇸 Español" },
  { id: "en", lbl: "🇬🇧 English" },
  { id: "pt", lbl: "🇧🇷 Português" },
];

function downloadHtml(html, fileName) {
  const blob = new Blob([html], { type: "text/html;charset=utf-8" });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(a.href), 4000);
}

/**
 * Tarjeta «Informe completo · descargable · ES / EN / PT» (spec v8, componentes 1 y 2).
 * El informe se genera al momento con los datos vivos: buildHtml(lang) → HTML autocontenido
 * (o una promesa de él, si antes hay que cargar datos).
 * El idioma elegido dura lo que la sesión en la página (no se guarda).
 */
export function DDReportCard({ variant, title, previewTitle, desc, features, note, buildHtml, fileName }) {
  const [lang, setLang] = useState("es");
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const generate = async (then) => {
    setBusy(true);
    setError("");
    try {
      then(await buildHtml(lang));
    } catch (err) {
      console.error("Error generando el informe", err);
      setError("No se pudo generar el informe. Revisá la conexión e intentá de nuevo.");
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    if (!preview) return undefined;
    const onKey = (e) => { if (e.key === "Escape") setPreview(null); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [preview]);

  const download = () => generate((html) => downloadHtml(html, fileName(lang)));

  return (
    <div className={`ddr-card ddr-card--${variant}`}>
      <h3>📄 {title}</h3>
      <div className="ddr-sub">{desc}</div>

      <div className="ddr-row">
        <span className="ddr-lbl">Idioma del informe</span>
        <div className="ddr-langs">
          {LANGS.map((l) => (
            <button key={l.id} className={lang === l.id ? "active" : ""} onClick={() => setLang(l.id)}>{l.lbl}</button>
          ))}
        </div>
      </div>

      <div className="ddr-actions">
        <button className="ddr-btn ddr-btn--pr" onClick={download} disabled={busy}>
          {busy ? "Generando…" : "⬇ Descargar informe (HTML)"}
        </button>
        <button className="ddr-btn" onClick={() => generate(setPreview)} disabled={busy}>👁 Vista previa</button>
        <span className="ddr-hint">El archivo incluye botón «Imprimir / guardar como PDF».</span>
      </div>

      {error && <div className="ddr-note ddr-note--err"><span>✕</span><div>{error}</div></div>}
      {note && <div className="ddr-note"><span>⚠</span><div>{note}</div></div>}

      {features && (
        <div className="ddr-feats">
          {features.map(([h, d]) => <div key={h} className="ddr-feat"><b>{h}</b>{d}</div>)}
        </div>
      )}

      {preview && (
        <div className="ddr-modal" onClick={(e) => { if (e.target === e.currentTarget) setPreview(null); }}>
          <div className="ddr-modal-box">
            <div className="ddr-modal-hd">
              <h3>{previewTitle}</h3>
              <div className="ddr-modal-btns">
                <button className="ddr-btn ddr-btn--pr" onClick={download} disabled={busy}>⬇ Descargar</button>
                <button className="ddr-btn" onClick={() => setPreview(null)}>✕ Cerrar</button>
              </div>
            </div>
            <iframe title={previewTitle} srcDoc={preview} />
          </div>
        </div>
      )}
    </div>
  );
}
